import React from 'react';

const globalStyles = `
  * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
  }

  .deck-wrapper {
      background-color: #030712;
      color: #f8fafc;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 40px;
      padding: 40px 0;
      font-family: 'Plus Jakarta Sans', sans-serif;
      -webkit-font-smoothing: antialiased;
      min-height: 100vh;
      width: 100%;
  }

  .mono {
      font-family: 'JetBrains Mono', monospace;
  }

  /* Slide Container Setup (Strict 1280x720 Presentation Canvas) */
  .slide-container {
      width: 1280px;
      height: 720px;
      min-width: 1280px;
      min-height: 720px;
      max-width: 1280px;
      max-height: 720px;
      background-color: #080c14;
      background-image: radial-gradient(circle at 85% 15%, rgba(99, 102, 241, 0.12) 0%, transparent 45%),
                        radial-gradient(circle at 15% 85%, rgba(245, 158, 11, 0.06) 0%, transparent 45%);
      border-radius: 16px;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.08);
      padding: 44px 56px;
      position: relative;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      transform-origin: top center;
  }
  
  /* Responsive scale for smaller screens (optional but good for previews) */
  @media (max-width: 1300px) {
    .slide-container {
      transform: scale(0.8);
      margin-bottom: -144px; /* Adjust for scaled gap */
    }
  }
  @media (max-width: 1050px) {
    .slide-container {
      transform: scale(0.6);
      margin-bottom: -288px;
    }
  }

  /* Appendix Container Variant */
  .slide-container.appendix-slide {
      border: 1px solid rgba(168, 85, 247, 0.25);
      background-image: radial-gradient(circle at 85% 15%, rgba(168, 85, 247, 0.12) 0%, transparent 45%),
                        radial-gradient(circle at 15% 85%, rgba(6, 182, 212, 0.06) 0%, transparent 45%);
  }

  /* Pill Badges */
  .pill-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 14px;
      border-radius: 20px;
      background: rgba(251, 191, 36, 0.08);
      border: 1px solid rgba(251, 191, 36, 0.3);
      color: #fbbf24;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      width: fit-content;
  }

  .pill-badge.purple {
      background: rgba(168, 85, 247, 0.08);
      border-color: rgba(168, 85, 247, 0.3);
      color: #c084fc;
  }

  .pill-badge.cyan {
      background: rgba(6, 182, 212, 0.08);
      border-color: rgba(6, 182, 212, 0.3);
      color: #22d3ee;
  }

  .pill-badge.red {
      background: rgba(239, 68, 68, 0.08);
      border-color: rgba(239, 68, 68, 0.3);
      color: #f87171;
  }

  .pill-badge.green {
      background: rgba(16, 185, 129, 0.08);
      border-color: rgba(16, 185, 129, 0.3);
      color: #34d399;
  }

  /* Slide Titles */
  .slide-title {
      font-size: 38px;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: -0.8px;
      line-height: 1.1;
      margin-top: 12px;
      margin-bottom: 22px;
  }

  .slide-title span {
      background: linear-gradient(135deg, #f97316 0%, #a855f7 50%, #38bdf8 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
  }

  /* Content Areas */
  .content-area {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;
  }

  /* Glass Cards */
  .glass-card {
      background: rgba(15, 23, 42, 0.6);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 12px;
      padding: 24px;
      backdrop-filter: blur(12px);
  }

  /* Value Banner */
  .value-banner {
      background: rgba(15, 23, 42, 0.7);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 10px;
      padding: 14px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 13px;
      color: #cbd5e1;
  }

  /* Footer Bar */
  .footer-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-top: 1px solid rgba(255, 255, 255, 0.08);
      padding-top: 14px;
      font-size: 11px;
      color: #64748b;
  }

  /* Slide 1 Hero */
  .title-hero {
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;
  }

  .title-hero h1 {
      font-size: 54px;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: -1.2px;
      line-height: 1.08;
      margin-top: 18px;
      margin-bottom: 14px;
  }

  .title-hero h1 span {
      background: linear-gradient(135deg, #f97316 0%, #a855f7 50%, #38bdf8 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
  }

  .title-hero .subtitle {
      font-size: 19px;
      color: #94a3b8;
      font-weight: 400;
      max-width: 860px;
      line-height: 1.45;
      margin-bottom: 36px;
  }

  .title-stats-grid {
      border-top: 1px solid rgba(255, 255, 255, 0.08);
      padding-top: 22px;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 32px;
  }

  .title-stat-col .label {
      font-size: 11px;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 6px;
  }

  .title-stat-col .val {
      font-size: 14px;
      font-weight: 600;
      color: #f1f5f9;
  }

  /* Slide 2 Eras Grid */
  .era-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 18px;
      width: 100%;
  }

  .era-card {
      background: rgba(15, 23, 42, 0.6);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 12px;
      padding: 22px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 330px;
  }

  .era-card.era-3 {
      border: 1.5px solid #06b6d4;
      background: linear-gradient(145deg, rgba(8, 47, 73, 0.4) 0%, rgba(15, 23, 42, 0.8) 100%);
      box-shadow: 0 0 25px rgba(6, 182, 212, 0.15);
  }

  .era-card .era-tag {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      margin-bottom: 8px;
  }

  .era-card h3 {
      font-size: 18px;
      color: #ffffff;
      font-weight: 700;
      margin-bottom: 4px;
  }

  .era-card .sub-desc {
      font-size: 12px;
      color: #94a3b8;
      margin-bottom: 16px;
  }

  .era-card ul {
      list-style: none;
      padding: 0;
      margin-bottom: 16px;
  }

  .era-card li {
      font-size: 12px;
      color: #cbd5e1;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 6px;
  }

  .era-card .footer-metric {
      font-size: 11px;
      font-weight: 700;
      padding-top: 12px;
      border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  /* Slide 3 Pain Points Grid */
  .pain-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;
      width: 100%;
  }

  .pain-card {
      background: rgba(15, 23, 42, 0.6);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-top: 3.5px solid #f87171;
      border-radius: 12px;
      padding: 20px;
      height: 330px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
  }

  .pain-card .step-num {
      font-size: 11px;
      font-weight: 700;
      color: #f87171;
      margin-bottom: 6px;
  }

  .pain-card h3 {
      font-size: 16px;
      font-weight: 700;
      color: #ffffff;
      margin-bottom: 8px;
  }

  .pain-card p {
      font-size: 12px;
      color: #94a3b8;
      line-height: 1.45;
      margin-bottom: 12px;
  }

  .pain-card ul {
      list-style: none;
      padding: 0;
  }

  .pain-card li {
      font-size: 11.5px;
      color: #cbd5e1;
      margin-bottom: 6px;
      display: flex;
      align-items: center;
      gap: 6px;
  }

  /* Team Cards */
  .team-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
      width: 100%;
  }

  .team-card {
      background: rgba(15, 23, 42, 0.6);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 12px;
      padding: 24px;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
  }

  .team-avatar {
      width: 90px;
      height: 90px;
      border-radius: 50%;
      object-fit: cover;
      margin-bottom: 16px;
      border: 2px solid #38bdf8;
      box-shadow: 0 0 15px rgba(56, 189, 248, 0.2);
  }

  .team-card h3 {
      font-size: 18px;
      font-weight: 700;
      color: #ffffff;
      margin-bottom: 4px;
  }

  .team-card .role-badge {
      font-size: 11px;
      font-weight: 700;
      color: #38bdf8;
      margin-bottom: 12px;
  }

  .team-card p {
      font-size: 12px;
      color: #94a3b8;
      line-height: 1.45;
  }
`;

export default function InvestmentPitch() {
  return (
    <div className="deck-wrapper">
      {/* Inject Fonts and Icons */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
      
      {/* Inject Styles */}
      <style>{globalStyles}</style>

      {}
      {/* SLIDE 1: Title Slide */}
      <div className="slide-container" id="slide1">
          <div className="title-hero">
              <div className="pill-badge">
                  <i className="fa-solid fa-layer-group"></i> Institutional Capital Operating System
              </div>
              <h1>Frontier: <span>Autonomous Capital Allocation</span></h1>
              <p className="subtitle">The Enterprise Cognitive Infrastructure for Underwriting, Board-Level Strategy, and Portfolio Asset Transformation.</p>
              
              <div className="title-stats-grid">
                  <div className="title-stat-col">
                      <div className="label mono">Addressable Opportunity</div>
                      <div className="val">$15T+ Private Capital AUM</div>
                  </div>
                  <div className="title-stat-col">
                      <div className="label mono">Cognitive Labor Spend</div>
                      <div className="val">$100B+ Annual External Bandwidth</div>
                  </div>
                  <div className="title-stat-col">
                      <div className="label mono">Core Architecture</div>
                      <div className="val">Context-Locked Sovereign Workflows</div>
                  </div>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 1 / 10 (Core)</span>
          </div>
      </div>

      {}
      {/* SLIDE 2: Category Creation (The 3 Eras) */}
      <div className="slide-container" id="slide2">
          <div>
              <div className="pill-badge purple">
                  <i className="fa-solid fa-chart-line"></i> Category Vision
              </div>
              <h2 className="slide-title">The 3 Eras of <span>Private Market Technology</span></h2>
          </div>

          <div className="content-area">
              <div className="era-grid" style={{ marginBottom: '18px' }}>
                  {/* Era 1 */}
                  <div className="era-card">
                      <div>
                          <div className="era-tag mono" style={{ color: '#64748b' }}>ERA 1: DATA SUBSTRATE</div>
                          <h3>Raw Data & VDRs</h3>
                          <div className="sub-desc">PitchBook, CapIQ, Datasite, Excel</div>
                          <ul>
                              <li><i className="fa-solid fa-circle" style={{ fontSize: '5px', color: '#64748b' }}></i> Stores raw unstructured data</li>
                              <li><i className="fa-solid fa-circle" style={{ fontSize: '5px', color: '#64748b' }}></i> 100% manual human extraction</li>
                              <li><i className="fa-solid fa-circle" style={{ fontSize: '5px', color: '#64748b' }}></i> Extreme analytical latency</li>
                          </ul>
                      </div>
                      <div className="footer-metric mono" style={{ color: '#64748b' }}>Manual Analyst Labor</div>
                  </div>

                  {/* Era 2 */}
                  <div className="era-card">
                      <div>
                          <div className="era-tag mono" style={{ color: '#f87171' }}>ERA 2: RETRIEVAL SAAS</div>
                          <h3>Generic AI & CRMs</h3>
                          <div className="sub-desc">Wrapper Scripts & Passive Tools</div>
                          <ul>
                              <li><i className="fa-solid fa-circle" style={{ fontSize: '5px', color: '#f87171' }}></i> Basic document summarization</li>
                              <li><i className="fa-solid fa-circle" style={{ fontSize: '5px', color: '#f87171' }}></i> Financial hallucination & drift</li>
                              <li><i className="fa-solid fa-circle" style={{ fontSize: '5px', color: '#f87171' }}></i> Zero deterministic math integrity</li>
                          </ul>
                      </div>
                      <div className="footer-metric mono" style={{ color: '#f87171' }}>Unsafe for IC Decisions</div>
                  </div>

                  {/* Era 3 */}
                  <div className="era-card era-3">
                      <div>
                          <div className="era-tag mono" style={{ color: '#22d3ee' }}>ERA 3: COGNITIVE LAYER</div>
                          <h3>Frontier Sovereign AI</h3>
                          <div className="sub-desc" style={{ color: '#22d3ee' }}>Autonomous Underwriting Engines</div>
                          <ul>
                              <li><i className="fa-solid fa-circle" style={{ fontSize: '5px', color: '#22d3ee' }}></i> Autonomous 48-hr CDD synthesis</li>
                              <li><i className="fa-solid fa-circle" style={{ fontSize: '5px', color: '#22d3ee' }}></i> Dynamic LBOs & Math Solvers</li>
                              <li><i className="fa-solid fa-circle" style={{ fontSize: '5px', color: '#22d3ee' }}></i> Adversarial IC Stress-Testing</li>
                          </ul>
                      </div>
                      <div className="footer-metric mono" style={{ color: '#22d3ee' }}>$50B+ Software TAM</div>
                  </div>
              </div>

              <div className="value-banner">
                  <p><strong style={{ color: '#fbbf24' }}>Structural Shift:</strong> Private capital spends $100B+ annually on external diligence retainers and analyst bandwidth. Frontier transforms manual analytical labor into sovereign software.</p>
                  <span className="mono" style={{ fontSize: '11px', color: '#22d3ee', fontWeight: 700 }}>Era 3 Value Capture</span>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 2 / 10 (Core)</span>
          </div>
      </div>

      {}
      {/* SLIDE 3: The Growth Capital Bottleneck */}
      <div className="slide-container" id="slide3">
          <div>
              <div className="pill-badge red">
                  <i className="fa-solid fa-triangle-exclamation"></i> Deal Lifecycle Friction
              </div>
              <h2 className="slide-title">The Private Equity <span>Growth Bottleneck</span></h2>
          </div>

          <div className="content-area">
              <div className="pain-grid" style={{ marginBottom: '18px' }}>
                  {/* Sourcing */}
                  <div className="pain-card">
                      <div>
                          <div className="step-num mono">STAGE 01 // SOURCING</div>
                          <h3>Reactive Pipeline</h3>
                          <p>Growth teams manually screen databases and intermediary teasers, reacting to deal flow rather than proactively sighting proprietary targets.</p>
                      </div>
                      <ul>
                          <li><i className="fa-solid fa-xmark" style={{ color: '#f87171' }}></i> Linear analyst sighting</li>
                          <li><i className="fa-solid fa-xmark" style={{ color: '#f87171' }}></i> Missed off-market targets</li>
                      </ul>
                  </div>

                  {/* Diligence */}
                  <div className="pain-card">
                      <div>
                          <div className="step-num mono">STAGE 02 // DILIGENCE</div>
                          <h3>4-6 Week CDD Sprints</h3>
                          <p>Deal teams spend $500k+ on consulting incumbents to read expert transcripts and spot-check small VDR samples—frequently missing critical churn signals.</p>
                      </div>
                      <ul>
                          <li><i className="fa-solid fa-xmark" style={{ color: '#f87171' }}></i> $500k+ MBB retainers</li>
                          <li><i className="fa-solid fa-xmark" style={{ color: '#f87171' }}></i> Sample-based data room reviews</li>
                      </ul>
                  </div>

                  {/* IC Debate */}
                  <div className="pain-card">
                      <div>
                          <div className="step-num mono">STAGE 03 // IC DEBATE</div>
                          <h3>Confirmation Bias</h3>
                          <p>IC memos suffer from deal-team confirmation bias. Real adversarial stress-testing is limited to short committee discussions rather than multi-scenario simulations.</p>
                      </div>
                      <ul>
                          <li><i className="fa-solid fa-xmark" style={{ color: '#f87171' }}></i> Superficial bear cases</li>
                          <li><i className="fa-solid fa-xmark" style={{ color: '#f87171' }}></i> Broken cell references in math</li>
                      </ul>
                  </div>

                  {/* Value Creation */}
                  <div className="pain-card">
                      <div>
                          <div className="step-num mono">STAGE 04 // VALUE CREATION</div>
                          <h3>Stalled Top-Line Growth</h3>
                          <p>Revenue expansion is the primary driver of growth equity returns. Yet post-close GTM optimization, pricing power, and bolt-on M&A stall due to analytical bottlenecks.</p>
                      </div>
                      <ul>
                          <li><i className="fa-solid fa-xmark" style={{ color: '#f87171' }}></i> Slow TAM expansion</li>
                          <li><i className="fa-solid fa-xmark" style={{ color: '#f87171' }}></i> Delayed add-on origination</li>
                      </ul>
                  </div>
              </div>

              <div className="value-banner">
                  <p><strong style={{ color: '#f87171' }}>The Problem:</strong> Standard generative AI wrappers fail because they treat numbers as unstructured text tokens, leading to hallucinated multiples and broken financial models.</p>
                  <span className="mono" style={{ fontSize: '11px', color: '#f87171', fontWeight: 700 }}>Linear Human Ceiling</span>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 3 / 10 (Core)</span>
          </div>
      </div>

      {}
      {/* SLIDE 4: Merged Platform Architecture & Technical Moat */}
      <div className="slide-container" id="slide4">
          <div>
              <div className="pill-badge cyan">
                  <i className="fa-solid fa-cubes"></i> Proprietary Architecture
              </div>
              <h2 className="slide-title">Full-Stack Platform & <span>Context-Locked Moat</span></h2>
          </div>

          <div className="content-area">
              <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '20px', width: '100%', marginBottom: '16px' }}>
                  {/* Left Stack Layers */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255, 255, 255, 0.08)', borderLeft: '4px solid #a855f7', borderRadius: '8px', padding: '12px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <h4 style={{ fontSize: '13.5px', fontWeight: 700, color: '#ffffff' }}>L5: AlphaEngine™</h4>
                              <span className="mono" style={{ fontSize: '10px', color: '#c084fc', fontWeight: 700 }}>AUTONOMOUS WORKFORCE</span>
                          </div>
                          <p style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '2px' }}>Thematic Sighting, 48-Hr CDD Synthesis, Dynamic LBOs, & GTM Scaling.</p>
                      </div>

                      <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255, 255, 255, 0.08)', borderLeft: '4px solid #38bdf8', borderRadius: '8px', padding: '12px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <h4 style={{ fontSize: '13.5px', fontWeight: 700, color: '#ffffff' }}>L4: SmartOrch™</h4>
                              <span className="mono" style={{ fontSize: '10px', color: '#38bdf8', fontWeight: 700 }}>CONTROL PLANE</span>
                          </div>
                          <p style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '2px' }}>Context-Locked Agentic DAGs, Deterministic Math Router, & MNPI Fencing.</p>
                      </div>

                      <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255, 255, 255, 0.08)', borderLeft: '4px solid #06b6d4', borderRadius: '8px', padding: '12px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <h4 style={{ fontSize: '13.5px', fontWeight: 700, color: '#ffffff' }}>L3: OmniContext™</h4>
                              <span className="mono" style={{ fontSize: '10px', color: '#06b6d4', fontWeight: 700 }}>PROVENANCE GRAPH</span>
                          </div>
                          <p style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '2px' }}>Cell-Coordinate Provenance, 100% VDR Ingestion, & Zero-Loss Financial Extraction.</p>
                      </div>

                      <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255, 255, 255, 0.08)', borderLeft: '4px solid #fbbf24', borderRadius: '8px', padding: '12px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <h4 style={{ fontSize: '13.5px', fontWeight: 700, color: '#ffffff' }}>L2: IC-SLM™</h4>
                              <span className="mono" style={{ fontSize: '10px', color: '#fbbf24', fontWeight: 700 }}>FINANCIAL REASONING</span>
                          </div>
                          <p style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '2px' }}>Growth Underwriting Logic, Debt Covenants, Unit Economics, & Churn Risk Models.</p>
                      </div>
                  </div>

                  {/* Right Moat Box */}
                  <div className="glass-card" style={{ border: '1.5px solid #06b6d4', background: 'linear-gradient(145deg, rgba(8, 47, 73, 0.4) 0%, rgba(15, 23, 42, 0.8) 100%)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                          <div className="mono" style={{ fontSize: '11px', color: '#22d3ee', fontWeight: 700, marginBottom: '6px' }}>FRONTIER ARCHITECTURAL BREAKTHROUGH</div>
                          <h3 style={{ fontSize: '18px', color: '#ffffff', marginBottom: '10px' }}>Context-Locked Workflows</h3>
                          <p style={{ fontSize: '12px', color: '#cbd5e1', lineHeight: 1.45, marginBottom: '14px' }}>Bypasses multi-agent hallucination decay by binding every agent to an immutable, mathematically verified context substrate.</p>
                          
                          <ul style={{ listStyle: 'none', padding: 0 }}>
                              <li style={{ fontSize: '12px', color: '#e2e8f0', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <i className="fa-solid fa-check" style={{ color: '#22d3ee' }}></i> <strong>Cell-Coordinate Lineage:</strong> Cryptographic source citations
                              </li>
                              <li style={{ fontSize: '12px', color: '#e2e8f0', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <i className="fa-solid fa-check" style={{ color: '#22d3ee' }}></i> <strong>Deterministic Math Solver:</strong> Calculations routed off LLMs
                              </li>
                              <li style={{ fontSize: '12px', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <i className="fa-solid fa-check" style={{ color: '#22d3ee' }}></i> <strong>Air-Gapped Sovereignty:</strong> Complete MNPI deal isolation
                              </li>
                          </ul>
                      </div>

                      <div className="mono" style={{ fontSize: '10.5px', color: '#22d3ee', background: 'rgba(6, 182, 212, 0.12)', padding: '8px', borderRadius: '6px', textAlign: 'center', marginTop: '10px' }}>
                          0.97 RAGAS Certified // 100% Mathematical Auditability
                      </div>
                  </div>
              </div>

              <div className="value-banner">
                  <p><strong style={{ color: '#22d3ee' }}>Sovereign Layer 1 Ingestion:</strong> Connects directly to existing market feeds (PitchBook, CapIQ, Tegus), VDRs (Datasite, Intralinks), and internal drives.</p>
                  <span className="mono" style={{ fontSize: '11px', color: '#c084fc', fontWeight: 700 }}>Zero Context Drift</span>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 4 / 10 (Core)</span>
          </div>
      </div>

      {}
      {/* SLIDE 5: Strategic ROI & Impact Metrics */}
      <div className="slide-container" id="slide5">
          <div>
              <div className="pill-badge">
                  <i className="fa-solid fa-gauge-high"></i> Value Creation
              </div>
              <h2 className="slide-title">Quantified Impact for <span>Private Capital Funds</span></h2>
          </div>

          <div className="content-area">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', width: '100%', marginBottom: '24px' }}>
                  <div className="glass-card" style={{ textAlign: 'center', borderTop: '4px solid #22d3ee', padding: '28px 20px' }}>
                      <div className="mono" style={{ fontSize: '56px', fontWeight: 800, color: '#22d3ee', lineHeight: 1 }}>10x</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', marginTop: '10px' }}>DEAL VELOCITY</div>
                      <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>Compress 6-week commercial due diligence cycles into 48 hours.</p>
                  </div>

                  <div className="glass-card" style={{ textAlign: 'center', borderTop: '4px solid #f87171', padding: '28px 20px' }}>
                      <div className="mono" style={{ fontSize: '56px', fontWeight: 800, color: '#f87171', lineHeight: 1 }}>90%</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', marginTop: '10px' }}>COST REDUCTION</div>
                      <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>Cut third-party commercial diligence spend ($200k–$500k) per transaction.</p>
                  </div>

                  <div className="glass-card" style={{ textAlign: 'center', borderTop: '4px solid #c084fc', padding: '28px 20px' }}>
                      <div className="mono" style={{ fontSize: '56px', fontWeight: 800, color: '#c084fc', lineHeight: 1 }}>100%</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', marginTop: '10px' }}>CONTEXT FIDELITY</div>
                      <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>Zero context drift with exact row, column, & cell coordinate provenance.</p>
                  </div>
              </div>

              <div className="value-banner">
                  <p><strong style={{ color: '#22d3ee' }}>Velocity as Alpha:</strong> Deal teams underwrite high-conviction theses and submit verified, diligence-backed IOIs/LOIs before competing bidders finish initial data room reviews.</p>
                  <span className="mono" style={{ fontSize: '11px', color: '#fbbf24', fontWeight: 700 }}>Structural Speed Advantage</span>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 5 / 10 (Core)</span>
          </div>
      </div>

      {}
      {/* SLIDE 6: Commercial Traction & Active Pipeline */}
      <div className="slide-container" id="slide6">
          <div>
              <div className="pill-badge green">
                  <i className="fa-solid fa-chart-line"></i> Commercial Traction
              </div>
              <h2 className="slide-title">Institutional Validation & <span>Pipeline Momentum</span></h2>
          </div>

          <div className="content-area">
              {/* Top Key Metrics Bar */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', width: '100%', marginBottom: '24px' }}>
                  <div className="glass-card" style={{ textAlign: 'center', borderTop: '4px solid #fbbf24', padding: '24px' }}>
                      <div className="mono" style={{ fontSize: '48px', fontWeight: 800, color: '#fbbf24', lineHeight: 1 }}>$350K+</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', marginTop: '8px' }}>Average Initial ACV</div>
                      <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>High contract depth across buy-side & advisory</p>
                  </div>
                  <div className="glass-card" style={{ textAlign: 'center', borderTop: '4px solid #22d3ee', padding: '24px' }}>
                      <div className="mono" style={{ fontSize: '48px', fontWeight: 800, color: '#22d3ee', lineHeight: 1 }}>4 Hours</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', marginTop: '8px' }}>Speed to IC Memo</div>
                      <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Compressed from 3 weeks of manual analyst labor</p>
                  </div>
                  <div className="glass-card" style={{ textAlign: 'center', borderTop: '4px solid #34d399', padding: '24px' }}>
                      <div className="mono" style={{ fontSize: '48px', fontWeight: 800, color: '#34d399', lineHeight: 1 }}>100%</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', marginTop: '8px' }}>Pilot Conversion Rate</div>
                      <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Rapid conversion from deployment to software ARR</p>
                  </div>
              </div>

              {/* Active Pipeline Grid */}
              <div style={{ width: '100%', background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px', padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px' }}>
                      <span className="mono" style={{ fontSize: '12px', color: '#34d399', fontWeight: 700, textTransform: 'uppercase' }}>Active Institutional Engagements & Pipeline</span>
                      <span className="mono" style={{ fontSize: '11px', color: '#64748b' }}>Tier 1 Buy-Side & Transaction Advisory</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px' }}>
                      <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff' }}>e& Capital</div>
                          <div style={{ fontSize: '11px', color: '#34d399' }} className="mono">VC / Growth Capital</div>
                      </div>
                      <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff' }}>Roland Berger</div>
                          <div style={{ fontSize: '11px', color: '#34d399' }} className="mono">Transaction & Investor Services</div>
                      </div>
                      <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff' }}>Woodside Capital</div>
                          <div style={{ fontSize: '11px', color: '#34d399' }} className="mono">M&A Investment Banking</div>
                      </div>
                      <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff' }}>LIG Aerospace</div>
                          <div style={{ fontSize: '11px', color: '#34d399' }} className="mono">Corporate M&A / Strategy</div>
                      </div>
                  </div>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 6 / 10 (Core)</span>
          </div>
      </div>

      {}
      {/* SLIDE 7: Competitive Landscape & Category Funding Momentum */}
      <div className="slide-container" id="slide7">
          <div>
              <div className="pill-badge purple">
                  <i className="fa-solid fa-compass"></i> Market Positioning
              </div>
              <h2 className="slide-title">Category Validation & <span>Peer Funding Momentum</span></h2>
          </div>

          <div className="content-area">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', width: '100%' }}>
                  {/* Peer Set 1 */}
                  <div className="glass-card" style={{ borderLeft: '4px solid #a855f7' }}>
                      <div className="mono" style={{ fontSize: '11px', color: '#c084fc', fontWeight: 700, marginBottom: '6px' }}>PURE AI CO-PILOTS (TIER 3 SEARCH)</div>
                      <h3 style={{ fontSize: '16px', color: '#ffffff', marginBottom: '6px' }}>Rogo & Hebbia</h3>
                      <div style={{ fontSize: '11px', color: '#fbbf24', fontFamily: "'JetBrains Mono', monospace", marginBottom: '8px' }}>
                          Rogo ($310M+ Raised @ $2B Val) | Hebbia ($130M+ Raised @ $700M+ Val)
                      </div>
                      <p style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.45 }}>Validates massive software demand for financial AI. However, they remain build-for-analyst search tools lacking cell-coordinate math solvers and forward-deployed execution.</p>
                  </div>

                  {/* Peer Set 2 */}
                  <div className="glass-card" style={{ borderLeft: '4px solid #38bdf8' }}>
                      <div className="mono" style={{ fontSize: '11px', color: '#38bdf8', fontWeight: 700, marginBottom: '6px' }}>MARKET TERMINALS & TRANSCRIPTS (TIER 1)</div>
                      <h3 style={{ fontSize: '16px', color: '#ffffff', marginBottom: '6px' }}>AlphaSense & Tegus</h3>
                      <div style={{ fontSize: '11px', color: '#fbbf24', fontFamily: "'JetBrains Mono', monospace", marginBottom: '8px' }}>
                          AlphaSense ($600M+ Raised; Acquired Tegus for $930M)
                      </div>
                      <p style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.45 }}>Dominant raw data substrates. Essential feeds, but offer zero autonomous underwriting, dynamic 3-statement modeling, or board outcome delivery.</p>
                  </div>

                  {/* Peer Set 3 */}
                  <div className="glass-card" style={{ borderLeft: '4px solid #f87171' }}>
                      <div className="mono" style={{ fontSize: '11px', color: '#f87171', fontWeight: 700, marginBottom: '6px' }}>STRATEGY CONSULTANCIES (TIER 4 MANUAL)</div>
                      <h3 style={{ fontSize: '16px', color: '#ffffff', marginBottom: '6px' }}>McKinsey, Bain, Alvarez & Marsal</h3>
                      <div style={{ fontSize: '11px', color: '#f87171', fontFamily: "'JetBrains Mono', monospace", marginBottom: '8px' }}>
                          $500k+ CDD Retainers | Linear Headcount Scaling
                      </div>
                      <p style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.45 }}>High-touch board delivery, but scale linearly with expensive human headcount. Suffer from high friction billing and lack proprietary software engines.</p>
                  </div>

                  {/* Frontier */}
                  <div className="glass-card" style={{ background: 'linear-gradient(135deg, rgba(30, 27, 75, 0.9) 0%, rgba(88, 28, 135, 0.8) 100%)', border: '1.5px solid #fbbf24', boxShadow: '0 0 20px rgba(251, 191, 36, 0.2)' }}>
                      <div className="mono" style={{ fontSize: '11px', color: '#fbbf24', fontWeight: 700, marginBottom: '6px' }}>FRONTIER COGNITIVE AI LAYER</div>
                      <h3 style={{ fontSize: '16px', color: '#ffffff', marginBottom: '6px' }}>AlphaEngine + SmartOrch Control Plane</h3>
                      <div style={{ fontSize: '11px', color: '#22d3ee', fontFamily: "'JetBrains Mono', monospace", marginBottom: '8px' }}>
                          Context-Locked Math Integrity + Forward-Deployed Advisory
                      </div>
                      <p style={{ fontSize: '12px', color: '#f1f5f9', lineHeight: 1.45 }}>Combines high-margin software ARR with 0.97 RAGAS certified context precision and custom DAG workflow lock-in. Captures $350k–$1M+ institutional ACVs.</p>
                  </div>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 7 / 10 (Core)</span>
          </div>
      </div>

      {}
      {/* SLIDE 8: Business Model & Unit Economics */}
      <div className="slide-container" id="slide8">
          <div>
              <div className="pill-badge">
                  <i className="fa-solid fa-calculator"></i> Business Model
              </div>
              <h2 className="slide-title">Predictable Software ARR & <span>Unit Economics</span></h2>
          </div>

          <div className="content-area">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '24px', width: '100%' }}>
                  {/* Left Stack */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div className="glass-card" style={{ borderLeft: '4px solid #10b981', padding: '18px 22px' }}>
                          <div className="mono" style={{ fontSize: '11px', color: '#10b981', fontWeight: 700, marginBottom: '4px' }}>TARGET GROSS MARGINS</div>
                          <div style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff' }}>75% – 80% Blended</div>
                          <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>85%+ Gross Margins on AlphaEngine SaaS; 55%+ on Forward-Deployed Advisory.</p>
                      </div>

                      <div className="glass-card" style={{ borderLeft: '4px solid #22d3ee', padding: '18px 22px' }}>
                          <div className="mono" style={{ fontSize: '11px', color: '#22d3ee', fontWeight: 700, marginBottom: '4px' }}>NET DOLLAR RETENTION (NDR)</div>
                          <div style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff' }}>&gt;130% Target</div>
                          <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>Driven by lifecycle module expansion (Sourcing &rarr; CDD &rarr; LBO &rarr; PortCo Growth).</p>
                      </div>

                      <div className="glass-card" style={{ borderLeft: '4px solid #c084fc', padding: '18px 22px' }}>
                          <div className="mono" style={{ fontSize: '11px', color: '#c084fc', fontWeight: 700, marginBottom: '4px' }}>LTV : CAC RATIO</div>
                          <div style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff' }}>5.2x Projected</div>
                          <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>High customer lifetime value anchored by institutional multi-year contracts.</p>
                      </div>
                  </div>

                  {/* Right Box */}
                  <div className="glass-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', border: '1.5px solid rgba(251, 191, 36, 0.4)' }}>
                      <div>
                          <div className="mono" style={{ fontSize: '11px', color: '#fbbf24', fontWeight: 700, marginBottom: '8px' }}>LAND & EXPAND TROJAN HORSE GTM</div>
                          <h3 style={{ fontSize: '20px', color: '#ffffff', marginBottom: '12px' }}>Transition to High-Margin SaaS Mix</h3>
                          <p style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.5, marginBottom: '20px' }}>Forward-deployed advisory serves as an upfront customer acquisition vehicle that converts rapidly into recurring software licenses.</p>
                          
                          <div>
                              <div style={{ marginBottom: '12px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#ffffff', marginBottom: '4px' }}>
                                      <span className="mono">Year 1 Revenue Mix</span>
                                      <span className="mono">50% SaaS / 50% Advisory</span>
                                  </div>
                                  <div style={{ height: '8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', overflow: 'hidden', display: 'flex' }}>
                                      <div style={{ width: '50%', background: '#22d3ee' }}></div>
                                      <div style={{ width: '50%', background: '#c084fc' }}></div>
                                  </div>
                              </div>

                              <div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#ffffff', marginBottom: '4px' }}>
                                      <span className="mono">Year 3 Target Mix</span>
                                      <span className="mono">80% SaaS / 20% Advisory</span>
                                  </div>
                                  <div style={{ height: '8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', overflow: 'hidden', display: 'flex' }}>
                                      <div style={{ width: '80%', background: '#22d3ee' }}></div>
                                      <div style={{ width: '20%', background: '#c084fc' }}></div>
                                  </div>
                              </div>
                          </div>
                      </div>

                      <div className="mono" style={{ fontSize: '11px', color: '#fbbf24', background: 'rgba(251, 191, 36, 0.1)', padding: '10px', borderRadius: '6px', textAlign: 'center', marginTop: '20px' }}>
                          Predictable Software Economics + High-Touch Contract Scale
                      </div>
                  </div>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 8 / 10 (Core)</span>
          </div>
      </div>

      {}
      {/* SLIDE 9: Executive Leadership & Team */}
      <div className="slide-container" id="slide9">
          <div>
              <div className="pill-badge purple">
                  <i className="fa-solid fa-users"></i> Executive Leadership
              </div>
              <h2 className="slide-title">Institutional AI & <span>Buy-Side Operational Leadership</span></h2>
          </div>

          <div className="content-area">
              <div className="team-grid" style={{ marginBottom: '20px' }}>
                  {/* Balbinder */}
                  <div className="team-card" style={{ borderTop: '3px solid #38bdf8' }}>
                      <img className="team-avatar" alt="Balbinder Singh Bhatia" src="https://media.licdn.com/dms/image/v2/D4D03AQH9AJJ2wiz6WA/profile-displayphoto-crop_800_800/B4DZruW6OgHwAI-/0/1764935577241?e=1788393600&v=beta&t=g7h1-_p5z9-YOPr5lzgHiB3QszU0JeB4VNTC8V0S2XQ" />
                      <h3>Balbinder Singh Bhatia</h3>
                      <div className="role-badge mono">Ex-PE & M&A</div>
                      <p>Led $1B+ institutional deal executions across private equity and corporate M&A. Deep expertise in fund governance, deal underwriting, and IC memos.</p>
                  </div>

                  {/* Zuhaib */}
                  <div className="team-card" style={{ borderTop: '3px solid #c084fc' }}>
                      <img className="team-avatar" style={{ borderColor: '#c084fc', boxShadow: '0 0 15px rgba(192, 132, 252, 0.2)' }} alt="Zuhaib Ahmed" src="https://media.licdn.com/dms/image/v2/D5603AQGUcHRzPGPxvA/profile-displayphoto-crop_800_800/B56ZkHcCh6IAAM-/0/1756766415796?e=1788393600&v=beta&t=UN5aAwi-F9psr9wSCOQrQSevE6LlQGSu2SZDjel9A8c" />
                      <h3>Zuhaib Ahmed</h3>
                      <div className="role-badge mono" style={{ color: '#c084fc' }}>Ex-Graph AI Architect</div>
                      <p>Pioneered multi-agent runtime frameworks and Graph RAG architectures. Author of fundamental research papers in LLM reasoning & context graphs.</p>
                  </div>

                  {/* Maheshwar */}
                  <div className="team-card" style={{ borderTop: '3px solid #fbbf24' }}>
                      <img className="team-avatar" style={{ borderColor: '#fbbf24', boxShadow: '0 0 15px rgba(251, 191, 36, 0.2)' }} alt="Maheshwar Damojipurapu" src="https://media.licdn.com/dms/image/v2/D5603AQHSt29zIt-Ywg/profile-displayphoto-crop_800_800/B56Zwkk_eyIwAI-/0/1770140204184?e=1788393600&v=beta&t=l_IPClIHo9y0XCyPpTyvGEiTVN7tgdATQ9TzFzm7YcI" />
                      <h3>Maheshwar Damojipurapu</h3>
                      <div className="role-badge mono" style={{ color: '#fbbf24' }}>Ex-Strategy & Innovation Lead</div>
                      <p>Former Tier-1 strategy & innovation lead. Specialized in commercial due diligence (CDD), digital transformation, and forward-deployed AI deployments.</p>
                  </div>
              </div>

              <div className="value-banner">
                  <p><strong style={{ color: '#c084fc' }}>Advisory Board:</strong> Senior Managing Directors & Partners from top-tier Private Equity funds, Strategy consultancies, and AI Research Labs.</p>
                  <span className="mono" style={{ fontSize: '11px', color: '#fbbf24', fontWeight: 700 }}>Dual Domain Advantage</span>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 9 / 10 (Core)</span>
          </div>
      </div>

      {}
      {/* SLIDE 10: Funding Ask & Capital Allocation */}
      <div className="slide-container" id="slide10">
          <div>
              <div className="pill-badge purple">
                  <i className="fa-solid fa-rocket"></i> Funding Round
              </div>
              <h2 className="slide-title">Series A Ask & <span>18-Month Milestones</span></h2>
          </div>

          <div className="content-area">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '24px', width: '100%' }}>
                  {/* Left Ask Box */}
                  <div className="glass-card" style={{ border: '1.5px solid #fbbf24', background: 'linear-gradient(145deg, rgba(30, 27, 75, 0.7) 0%, rgba(15, 23, 42, 0.9) 100%)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                          <div className="mono" style={{ fontSize: '11px', color: '#fbbf24', fontWeight: 700, marginBottom: '8px' }}>SERIES A FUNDING ROUND</div>
                          <div style={{ fontSize: '48px', fontWeight: 800, color: '#ffffff', lineHeight: 1, marginBottom: '12px' }}>$15 Million</div>
                          <p style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: 1.5, marginBottom: '20px' }}>Accelerating OmniContext™ R&D, expanding forward-deployed AI engineering, and scaling institutional sales.</p>
                      </div>

                      <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#cbd5e1', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '6px', marginBottom: '10px' }}>
                              <span>OmniContext & SmartOrch Engine R&D</span>
                              <strong className="mono" style={{ color: '#22d3ee' }}>60%</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#cbd5e1', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '6px', marginBottom: '10px' }}>
                              <span>Forward-Deployed AI Engineers</span>
                              <strong className="mono" style={{ color: '#c084fc' }}>30%</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#cbd5e1' }}>
                              <span>Enterprise GTM & Distribution</span>
                              <strong className="mono" style={{ color: '#fbbf24' }}>10%</strong>
                          </div>
                      </div>
                  </div>

                  {/* Right Milestones Box */}
                  <div className="glass-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                          <div className="mono" style={{ fontSize: '11px', color: '#22d3ee', fontWeight: 700, marginBottom: '8px' }}>18-MONTH TARGET MILESTONES</div>
                          <h3 style={{ fontSize: '20px', color: '#ffffff', marginBottom: '16px' }}>Execution Roadmap</h3>
                          
                          <ul style={{ listStyle: 'none', padding: 0 }}>
                              <li style={{ fontSize: '13px', color: '#cbd5e1', marginBottom: '14px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                  <i className="fa-solid fa-circle-check" style={{ color: '#22d3ee', marginTop: '3px' }}></i>
                                  <div>
                                      <strong style={{ color: '#ffffff' }}>$10M+ ARR Threshold:</strong> Convert pipeline pilots into recurring multi-module enterprise contracts.
                                  </div>
                              </li>
                              <li style={{ fontSize: '13px', color: '#cbd5e1', marginBottom: '14px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                  <i className="fa-solid fa-circle-check" style={{ color: '#22d3ee', marginTop: '3px' }}></i>
                                  <div>
                                      <strong style={{ color: '#ffffff' }}>35+ Enterprise Funds:</strong> Expand coverage across Tier 1 PE buy-side and corporate strategy teams.
                                  </div>
                              </li>
                              <li style={{ fontSize: '13px', color: '#cbd5e1', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                  <i className="fa-solid fa-circle-check" style={{ color: '#22d3ee', marginTop: '3px' }}></i>
                                  <div>
                                      <strong style={{ color: '#ffffff' }}>US / UK / EMEA Expansion:</strong> Establish forward-deployed advisory hubs in New York, London, and Dubai.
                                  </div>
                              </li>
                          </ul>
                      </div>

                      <div className="mono" style={{ fontSize: '11px', color: '#22d3ee', background: 'rgba(6, 182, 212, 0.1)', padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                          Building the Standard Operating Architecture for Institutional Capital
                      </div>
                  </div>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 10 / 10 (Core Presentation End)</span>
          </div>
      </div>

      {}
      {/* SLIDE 11 (APPENDIX A): End-to-End Workflow Transformation Matrix */}
      <div className="slide-container appendix-slide" id="slide11">
          <div>
              <div className="pill-badge purple">
                  <i className="fa-solid fa-folder-open"></i> Appendix A // Workflow Deep Dive
              </div>
              <h2 className="slide-title">End-to-End <span>Deal Lifecycle Transformation</span></h2>
          </div>

          <div className="content-area">
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px', overflow: 'hidden', width: '100%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12.5px' }}>
                      <thead>
                          <tr style={{ background: 'rgba(30, 41, 59, 0.8)', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', color: '#cbd5e1' }}>
                              <th style={{ padding: '12px 16px' }} className="mono">PHASE</th>
                              <th style={{ padding: '12px 16px' }} className="mono">TRADITIONAL HUMAN / POINT-TOOL MODEL</th>
                              <th style={{ padding: '12px 16px', color: '#22d3ee' }} className="mono">FRONTIER AUTONOMOUS COGNITIVE LAYER</th>
                          </tr>
                      </thead>
                      <style>{`
                        tbody tr:not(:last-child) {
                          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                        }
                      `}</style>
                      <tbody style={{ color: '#94a3b8' }}>
                          <tr>
                              <td style={{ padding: '10px 16px', fontWeight: 700, color: '#ffffff' }}>01. Sighting</td>
                              <td style={{ padding: '10px 16px' }}>Keyword database searches; reactive review of intermediary teasers.</td>
                              <td style={{ padding: '10px 16px', color: '#e2e8f0' }}><strong style={{ color: '#22d3ee' }}>Autonomous Sighting Agents:</strong> Continuous market & hiring velocity monitoring.</td>
                          </tr>
                          <tr>
                              <td style={{ padding: '10px 16px', fontWeight: 700, color: '#ffffff' }}>02. CDD & Audit</td>
                              <td style={{ padding: '10px 16px' }}>4-6 week consulting sprints; $500k+ fees; sample-based expert reviews.</td>
                              <td style={{ padding: '10px 16px', color: '#e2e8f0' }}><strong style={{ color: '#22d3ee' }}>48-Hour Autonomous CDD:</strong> Ingests 100% of transcripts & VDRs with cell-level citations.</td>
                          </tr>
                          <tr>
                              <td style={{ padding: '10px 16px', fontWeight: 700, color: '#ffffff' }}>03. LBO Modeling</td>
                              <td style={{ padding: '10px 16px' }}>Manual spreadsheet updates; brittle formula entry; high cell error risk.</td>
                              <td style={{ padding: '10px 16px', color: '#e2e8f0' }}><strong style={{ color: '#22d3ee' }}>Dynamic LBO Synthesis:</strong> Auditable multi-tab Excel models with solver math integrity.</td>
                          </tr>
                          <tr>
                              <td style={{ padding: '10px 16px', fontWeight: 700, color: '#ffffff' }}>04. IC Debate</td>
                              <td style={{ padding: '10px 16px' }}>Selective presentations; committee meetings prone to confirmation bias.</td>
                              <td style={{ padding: '10px 16px', color: '#e2e8f0' }}><strong style={{ color: '#22d3ee' }}>Adversarial Bull/Bear Agents:</strong> Stress-tests growth, churn, and downside cases.</td>
                          </tr>
                          <tr>
                              <td style={{ padding: '10px 16px', fontWeight: 700, color: '#ffffff' }}>05. PortCo Growth</td>
                              <td style={{ padding: '10px 16px' }}>Manual GTM reviews; lagging quarterly reports; slow add-on M&A.</td>
                              <td style={{ padding: '10px 16px', color: '#e2e8f0' }}><strong style={{ color: '#22d3ee' }}>Growth Strategy Engine:</strong> Maps pricing power, ICP vectors, and bolt-on targets.</td>
                          </tr>
                      </tbody>
                  </table>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 11 // Appendix A</span>
          </div>
      </div>

      {}
      {/* SLIDE 12 (APPENDIX B): Strategic Acquisition Pathways */}
      <div className="slide-container appendix-slide" id="slide12">
          <div>
              <div className="pill-badge purple">
                  <i className="fa-solid fa-handshake-angle"></i> Appendix B // M&A Exit Outlets
              </div>
              <h2 className="slide-title">Strategic <span>Acquisition Pathways</span></h2>
          </div>

          <div className="content-area">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', width: '100%' }}>
                  {/* Pathway 1 */}
                  <div className="glass-card" style={{ borderTop: '4px solid #fbbf24' }}>
                      <div className="mono" style={{ fontSize: '11px', color: '#fbbf24', fontWeight: 700, marginBottom: '8px' }}>PATHWAY 1</div>
                      <h3 style={{ fontSize: '18px', color: '#ffffff', marginBottom: '8px' }}>Financial Data Terminals</h3>
                      <div style={{ fontSize: '12px', color: '#38bdf8', fontFamily: "'JetBrains Mono', monospace", marginBottom: '12px' }}>S&P Global, PitchBook, LSEG, FactSet</div>
                      <p style={{ fontSize: '12.5px', color: '#94a3b8', lineHeight: 1.45 }}>Acquiring Era 3 cognitive engines to prevent terminal commoditization and convert static data feeds into automated IC memos.</p>
                  </div>

                  {/* Pathway 2 */}
                  <div className="glass-card" style={{ borderTop: '4px solid #22d3ee' }}>
                      <div className="mono" style={{ fontSize: '11px', color: '#22d3ee', fontWeight: 700, marginBottom: '8px' }}>PATHWAY 2</div>
                      <h3 style={{ fontSize: '18px', color: '#ffffff', marginBottom: '8px' }}>Dealmaking CRMs</h3>
                      <div style={{ fontSize: '12px', color: '#22d3ee', fontFamily: "'JetBrains Mono', monospace", marginBottom: '12px' }}>Intapp / DealCloud, Salesforce</div>
                      <p style={{ fontSize: '12.5px', color: '#94a3b8', lineHeight: 1.45 }}>Upgrading passive relationship CRMs into active cognitive execution platforms across 70%+ of buy-side PE funds.</p>
                  </div>

                  {/* Pathway 3 */}
                  <div className="glass-card" style={{ borderTop: '4px solid #c084fc' }}>
                      <div className="mono" style={{ fontSize: '11px', color: '#c084fc', fontWeight: 700, marginBottom: '8px' }}>PATHWAY 3</div>
                      <h3 style={{ fontSize: '18px', color: '#ffffff', marginBottom: '8px' }}>Global Consultancies</h3>
                      <div style={{ fontSize: '12px', color: '#c084fc', fontFamily: "'JetBrains Mono', monospace", marginBottom: '12px' }}>Accenture, McKinsey, Bain, A&M</div>
                      <p style={{ fontSize: '12.5px', color: '#94a3b8', lineHeight: 1.45 }}>Acquiring productized software engines to scale commercial due diligence (CDD) practices without linear headcount growth.</p>
                  </div>
              </div>

              <div className="value-banner" style={{ marginTop: '24px' }}>
                  <p><strong style={{ color: '#fbbf24' }}>Multiple Strategic Outlets:</strong> High-margin recurring software ARR combined with deep institutional workflow lock-in creates asymmetric exit options.</p>
                  <span className="mono" style={{ fontSize: '11px', color: '#38bdf8', fontWeight: 700 }}>High Exit Valuation Multiples</span>
              </div>
          </div>

          <div className="footer-bar">
              <span>Confidential & Proprietary</span>
              <span className="mono">Frontier Technologies, Inc.</span>
              <span>Slide 12 // Appendix B</span>
          </div>
      </div>

    </div>
  );
}